/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

import { InputField, InputFieldProps, Size } from '@vaisala/rockhopper-components';
import React from 'react';
import CalculationController from '../../modules/calculator/CalculationController';

interface EnhancedInputProps extends InputFieldProps {
  onDelayedChange: (val: number) => void;
  min: number;
  max: number;
  setEditCondition: React.Dispatch<React.SetStateAction<'temperature' | 'pressure'  | 'elevation' | null>>;
  labelKey: string;
  hasrelations?: boolean;
}

const EnhancedInput = (props: EnhancedInputProps) => {
  const { onDelayedChange, value, min, max, setEditCondition, labelKey, hasrelations, ...rest } = props;
  const [localValue, setLocalValue] = React.useState(isNaN(value as any) ? 0 : value);
  const [updateTimeout, setUpdateTimeout] = React.useState<ReturnType<typeof setTimeout>>();
  const updateDelay = 2000;

  React.useEffect(() => {
    setLocalValue(toNum(value));
  }, [value]);

  const toNum = (val: string | number | undefined) => {
    const num = parseFloat(`${val}`);
    return isNaN(num) ? 0 : num;
  };

  const clearUpdateTimeout = () => {
    if (updateTimeout) clearTimeout(updateTimeout);
  };

  const onValueChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const val = evt.target.value.replace(',', '.').replace('+', '');
    if (evt.target.validity.valid || val === '' || val === '-') {
      setLocalValue(val);
    }
    clearUpdateTimeout();
    setUpdateTimeout(setTimeout(() => updateValue(val), updateDelay));
  };

  const updateValue = (val: string | number | undefined) => {
    setEditCondition(null);
    // restrict to min...max range
    let num = toNum(val);
    if (num < min) {
      num = min;
    }
    if (num > max) {
      num = max;
    }

    setLocalValue(CalculationController.trimValue(num));
    clearUpdateTimeout();
    onDelayedChange(num);
  };

  const onKeyDown = (evt: React.KeyboardEvent<HTMLInputElement>) => {
    if (evt.key === 'Enter') {
      (evt.target as any).blur();
    }
  };

  const updateFocus = () => {
    if (hasrelations) {
      setEditCondition(labelKey.substring('param.'.length) as 'temperature' | 'pressure' | 'elevation');
    }
  };

  return (
    <InputField
      type="text"
      value={localValue}
      width={Size.XS}
      invalid={false}
      pattern="^[+\-]?[0-9]\d*[\.,]?\d*$"
      onChange={(evt) => onValueChange(evt)}
      onBlur={() => updateValue(localValue)}
      onKeyDown={onKeyDown}
      onFocus={() => updateFocus()}
      {...rest}
    />
  );
};

export default EnhancedInput;
